import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Bell, AlertTriangle, CheckCircle } from 'lucide-react';
import { alertsApi } from '../../api/endpoints/alerts';
import { Card, StatCard } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Table, Thead, Th, Td, Tr } from '../../components/ui/Table';
import { Pagination } from '../../components/ui/Pagination';
import { PageSpinner } from '../../components/ui/Spinner';
import { formatDate } from '../../utils/formatters';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const CHART_COLORS = {
  cpu: '#ef4444',
  memory: '#eab308',
  disk: '#3b82f6',
  success: '#22c55e',
  error: '#ef4444',
};

const PARTNER_COLORS = ['#8b5cf6', '#3b82f6', '#14b8a6', '#f59e0b', '#ef4444', '#ec4899', '#6366f1', '#06b6d4'];

function useIsDark() {
  return document.documentElement.classList.contains('dark');
}

const getTooltipProps = () => {
  const isDark = document.documentElement.classList.contains('dark');
  return {
    contentStyle: {
      fontSize: '12px',
      borderRadius: '6px',
      border: isDark ? '1px solid #4b5563' : '1px solid #e5e7eb',
      backgroundColor: isDark ? '#111827' : '#ffffff',
      color: isDark ? '#f3f4f6' : '#111827',
      boxShadow: isDark ? '0 4px 12px rgba(0,0,0,0.4)' : '0 4px 12px rgba(0,0,0,0.1)',
    } as React.CSSProperties,
    labelStyle: {
      color: isDark ? '#f9fafb' : '#111827',
      fontWeight: 600,
    } as React.CSSProperties,
    itemStyle: {
      color: isDark ? '#d1d5db' : '#374151',
    } as React.CSSProperties,
    cursor: { fill: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)' },
  };
};

export function AnalyticsPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState('');
  const [metricFilter, setMetricFilter] = useState('');
  const [severityFilter, setSeverityFilter] = useState('');
  const [violationsHours, setViolationsHours] = useState(24);
  const [apiHours, setApiHours] = useState(24);
  const [healthHours, setHealthHours] = useState(24);

  const { data: rulesData } = useQuery({
    queryKey: ['alert-rules'],
    queryFn: () => alertsApi.listRules(),
  });

  const { data: historyData, isLoading: historyLoading } = useQuery({
    queryKey: ['alert-history', page, limit, search, metricFilter, severityFilter],
    queryFn: () => alertsApi.history({ page, limit, search, metric: metricFilter, severity: severityFilter }),
  });

  const { data: violationsData } = useQuery({
    queryKey: ['threshold-violations', violationsHours],
    queryFn: () => alertsApi.thresholdViolations(violationsHours),
  });

  const { data: apiStatsData } = useQuery({
    queryKey: ['partner-api-stats', apiHours],
    queryFn: () => alertsApi.partnerApiStats(apiHours),
  });

  const { data: healthStatsData } = useQuery({
    queryKey: ['partner-api-stats-health', healthHours],
    queryFn: () => alertsApi.partnerApiStats(healthHours),
  });

  const rules = rulesData?.rules || [];
  const history = historyData?.history || [];
  const totalAlerts = historyData?.total || 0;
  const totalPages = historyData?.totalPages || 1;
  const enabledRules = rules.filter(r => r.enabled).length;

  // Compute API health summary from perPartner data (overall doesn't have success/error counts)
  const healthPerPartner = (healthStatsData?.perPartner || []) as Array<{ partner_name: string; hour: string; success_count: number; error_count: number }>;
  const apiTotalSuccess = healthPerPartner.reduce((s, e) => s + (e.success_count || 0), 0);
  const apiTotalErrors = healthPerPartner.reduce((s, e) => s + (e.error_count || 0), 0);
  const apiTotal = apiTotalSuccess + apiTotalErrors;
  const errorRate = apiTotal > 0 ? ((apiTotalErrors / apiTotal) * 100).toFixed(1) : '0.0';

  // Doughnut chart data — always show both slices; if both zero show a placeholder
  const hasPieData = apiTotalSuccess > 0 || apiTotalErrors > 0;
  const pieData = hasPieData
    ? [
        { name: 'Success', value: apiTotalSuccess },
        { name: 'Errors', value: apiTotalErrors },
      ].filter(d => d.value > 0)
    : [{ name: 'No Data', value: 1 }];

  // Violations chart data
  const violationsChart = (violationsData?.data || []) as Array<{ hour: string; cpu: number; memory: number; disk: number }>;

  // Partner API bar chart data
  const perPartner = (apiStatsData?.perPartner || []) as Array<{ partner_name: string; hour: string; success_count: number; error_count: number }>;
  const partnerNames = [...new Set(perPartner.map(p => p.partner_name))];

  // Group by hour for stacked bar chart
  const barChartMap = new Map<string, Record<string, number | string>>();
  perPartner.forEach(entry => {
    const hour = entry.hour;
    if (!barChartMap.has(hour)) barChartMap.set(hour, {});
    const row = barChartMap.get(hour)!;
    row[entry.partner_name] = ((row[entry.partner_name] as number) || 0) + (entry.success_count || 0) + (entry.error_count || 0);
  });
  const barChartData = Array.from(barChartMap.entries()).map(([hour, counts]) => ({
    ...counts,
    hour: new Date(hour + 'Z').toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false }),
  }));

  if (historyLoading && !historyData) return <PageSpinner />;

  return (
    <div>
      <div className="mb-5">
        <h1 className="text-lg font-semibold text-gray-800 dark:text-white">Analytics</h1>
        <p className="text-gray-500 dark:text-gray-400 text-xs mt-0.5">Alert monitoring, threshold violations, and partner API analytics</p>
      </div>

      {/* Badge row */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-5">
        <div onClick={() => navigate('/analytics/rules')} className="cursor-pointer hover:shadow-md transition-shadow rounded-lg">
          <StatCard label="Escalation Rules" value={rules.length} icon={<Bell size={18} />} color="purple" />
        </div>
        <StatCard label="Alerts Triggered" value={totalAlerts} icon={<AlertTriangle size={18} />} color="red" />
        <StatCard label="Rules Enabled" value={enabledRules} icon={<CheckCircle size={18} />} color="green" />
      </div>

      {/* 70/30 grid */}
      <div className="grid grid-cols-1 lg:grid-cols-10 gap-5 mb-5">
        {/* Left: Alert History */}
        <div className="lg:col-span-7">
          <Card title="Alert History">
            {/* Filters */}
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800">
              <div className="flex flex-wrap gap-3 items-center">
                <input
                  type="text"
                  value={search}
                  onChange={e => { setSearch(e.target.value); setPage(1); }}
                  placeholder="Search rule or client..."
                  className="flex-1 min-w-[180px] px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-xs dark:bg-gray-700 dark:text-white focus:ring-1 focus:ring-purple-500"
                />
                <select
                  value={metricFilter}
                  onChange={e => { setMetricFilter(e.target.value); setPage(1); }}
                  className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-xs dark:bg-gray-700 dark:text-white"
                >
                  <option value="">All Metrics</option>
                  <option value="cpu_percent">CPU</option>
                  <option value="memory_percent">Memory</option>
                  <option value="disk_percent">Disk</option>
                </select>
                <select
                  value={severityFilter}
                  onChange={e => { setSeverityFilter(e.target.value); setPage(1); }}
                  className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-xs dark:bg-gray-700 dark:text-white"
                >
                  <option value="">All Severity</option>
                  <option value="warning">Warning</option>
                  <option value="critical">Critical</option>
                </select>
                <select
                  value={limit}
                  onChange={e => { setLimit(Number(e.target.value)); setPage(1); }}
                  className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-xs dark:bg-gray-700 dark:text-white"
                >
                  <option value={10}>10 / page</option>
                  <option value={25}>25 / page</option>
                  <option value={50}>50 / page</option>
                  <option value={100}>100 / page</option>
                </select>
              </div>
            </div>

            <Table>
              <Thead>
                <tr>
                  <Th>Time</Th>
                  <Th>Rule</Th>
                  <Th>Client</Th>
                  <Th>Metric</Th>
                  <Th>Value</Th>
                  <Th>Threshold</Th>
                  <Th>Severity</Th>
                  <Th>Notified</Th>
                </tr>
              </Thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                {history.map(entry => (
                  <Tr key={entry._id}>
                    <Td>{formatDate(entry.timestamp)}</Td>
                    <Td>
                      <span className="font-medium text-gray-800 dark:text-gray-200">{entry.rule_name}</span>
                    </Td>
                    <Td>
                      <div>
                        <span className="text-purple-600 dark:text-purple-400">{entry.client_name || entry.client_id}</span>
                        {entry.deployment_name && (
                          <p className="text-[10px] text-gray-400">{entry.deployment_name}</p>
                        )}
                      </div>
                    </Td>
                    <Td>
                      <Badge variant={
                        entry.metric === 'cpu_percent' ? 'red' :
                        entry.metric === 'memory_percent' ? 'yellow' : 'blue'
                      }>
                        {entry.metric.replace('_percent', '')}
                      </Badge>
                    </Td>
                    <Td>{Math.round(entry.value)}%</Td>
                    <Td>{entry.threshold}%</Td>
                    <Td>
                      <Badge variant={entry.severity === 'critical' ? 'red' : 'yellow'}>
                        {entry.severity}
                      </Badge>
                    </Td>
                    <Td>{entry.notifications_sent}</Td>
                  </Tr>
                ))}
                {history.length === 0 && (
                  <tr>
                    <td colSpan={8} className="text-center py-8 text-sm text-gray-500 dark:text-gray-400">
                      No alert history found
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>

            <div className="px-4 pb-3 flex items-center justify-between mt-2">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Showing {((page - 1) * limit) + 1}–{Math.min(page * limit, totalAlerts)} of {totalAlerts} alerts
              </p>
              <Pagination page={page} totalPages={totalPages} onPageChange={setPage} className="mt-0" />
            </div>
          </Card>
        </div>

        {/* Right: API Health */}
        <div className="lg:col-span-3">
          <Card title="API Health" action={
            <select
              value={healthHours}
              onChange={e => setHealthHours(Number(e.target.value))}
              className="text-xs border border-gray-200 dark:border-gray-600 rounded px-2 py-1 dark:bg-gray-700 dark:text-white"
            >
              <option value={24}>Last 24h</option>
              <option value={168}>Last 7d</option>
              <option value={720}>Last 30d</option>
            </select>
          }>
            <div className="p-4 space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <StatCard label="Total" value={apiTotal} color="blue" />
                <StatCard label="Success" value={apiTotalSuccess} color="green" />
                <StatCard label="Errors" value={apiTotalErrors} color="red" />
                <StatCard label="Error Rate" value={`${errorRate}%`} color={Number(errorRate) > 5 ? 'red' : 'green'} />
              </div>

              <div className="mt-4">
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={45}
                      outerRadius={75}
                      paddingAngle={hasPieData ? 2 : 0}
                      dataKey="value"
                    >
                      {pieData.map((entry) => (
                        <Cell
                          key={entry.name}
                          fill={
                            entry.name === 'Success' ? CHART_COLORS.success
                            : entry.name === 'Errors' ? CHART_COLORS.error
                            : '#e5e7eb'
                          }
                        />
                      ))}
                    </Pie>
                    {hasPieData && (
                      <Tooltip {...getTooltipProps()} />
                    )}
                    <Legend
                      wrapperStyle={{ fontSize: '11px' }}
                      formatter={(value: string) => (value === 'No Data' ? 'No data yet' : value)}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Devices Exceeding Limits */}
        <Card title="Devices Exceeding Limits" action={
          <select
            value={violationsHours}
            onChange={e => setViolationsHours(Number(e.target.value))}
            className="text-xs border border-gray-200 dark:border-gray-600 rounded px-2 py-1 dark:bg-gray-700 dark:text-white"
          >
            <option value={24}>Last 24h</option>
            <option value={168}>Last 7d</option>
            <option value={720}>Last 30d</option>
          </select>
        }>
          <div className="p-4">
            {violationsChart.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={violationsChart}>
                  <XAxis
                    dataKey="hour"
                    tickFormatter={(v: string) => new Date(v + 'Z').toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false })}
                    tick={{ fontSize: 11 }}
                    stroke="#9ca3af"
                  />
                  <YAxis tick={{ fontSize: 11 }} stroke="#9ca3af" />
                  <Tooltip
                    labelFormatter={(v: string) => formatDate(v + 'Z')}
                    {...getTooltipProps()}
                  />
                  <Legend wrapperStyle={{ fontSize: '11px' }} />
                  <Line type="monotone" dataKey="cpu" stroke={CHART_COLORS.cpu} name="CPU" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="memory" stroke={CHART_COLORS.memory} name="Memory" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="disk" stroke={CHART_COLORS.disk} name="Disk" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-xs text-gray-400 text-center py-12">No threshold violation data available</p>
            )}
          </div>
        </Card>

        {/* Partner API Analytics */}
        <Card title="Partner API Analytics" action={
          <select
            value={apiHours}
            onChange={e => setApiHours(Number(e.target.value))}
            className="text-xs border border-gray-200 dark:border-gray-600 rounded px-2 py-1 dark:bg-gray-700 dark:text-white"
          >
            <option value={24}>Last 24h</option>
            <option value={168}>Last 7d</option>
            <option value={720}>Last 30d</option>
          </select>
        }>
          <div className="p-4">
            {barChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={barChartData}>
                  <XAxis dataKey="hour" tick={{ fontSize: 11 }} stroke="#9ca3af" />
                  <YAxis tick={{ fontSize: 11 }} stroke="#9ca3af" />
                  <Tooltip {...getTooltipProps()} />
                  <Legend wrapperStyle={{ fontSize: '11px' }} />
                  {partnerNames.map((name, i) => (
                    <Bar
                      key={name}
                      dataKey={name}
                      stackId="partners"
                      fill={PARTNER_COLORS[i % PARTNER_COLORS.length]}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-xs text-gray-400 text-center py-12">No partner API data available</p>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
